import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AccountCircle, MoreVert } from '@mui/icons-material';
import { Home as HomeIcon } from '@mui/icons-material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Avatar, Box, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import slugify from 'slugify';
import { firmSummaryUrl, signInPath } from 'common/config/urls';
import { PORTFOLIO_VALUATION } from 'common/constants/auth';
import { AUDIT_LOGS_TAB } from 'common/constants/user';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { PageActionsProps } from 'components/ActionButtons/types';
import Logo from 'components/Logo';
import { NotificationBell } from 'components/NotificationCenter';
import { PrimaryNavigationIconButton, PrimaryNavigation as PrimaryNavigationPrimitive, SecondaryNavigation, Tooltip } from 'components/ui';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { NotificationCenterProvider } from 'context/NotificationCenterContext';
import { useThemeMode } from 'context/ThemeModeContext';
import { useZoom } from 'context/ZoomContext';
import {
  dashboardCompanyInformationUrl,
  dashboardCompanyOperationsUrl,
  dashboardCompanyOverviewUrl,
  dashboardCompanySettingsUrl,
  dashboardCompanySubscriptionUrl,
  WORKBOARD_ACCOUNT_SETTINGS_URL,
  WORKBOARD_ASSIGNMENT_RULES_URL,
  WORKBOARD_AUDIT_LOGS_URL,
  WORKBOARD_CURRENT_VALUATIONS_URL,
  WORKBOARD_MODELS_URL,
  WORKBOARD_OPPORTUNITIES_URL,
  WORKBOARD_PAST_VALUATIONS_URL,
} from 'dashboard409a/common/config/urls';
import {
  COMPANY_DETAILS_SLUGS,
  LOG_OUT,
  SETTINGS_SLUG,
  SUBSCRIPTION_SLUG,
  VIEW_DETAILS,
} from 'dashboard409a/common/constants';
import { IBottomMenuItems, INavigationItem, ITopMenuItems, Sidebar } from 'dashboard409a/components';
import DropboxOAuthCallbackHandler from 'dashboard409a/components/OperationsModal/DropboxOAuthCallbackHandler';
import { AttioValuationProvider } from 'dashboard409a/context/AttioValuationContext';
import { CompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { RootStateType } from 'dashboard409a/states/store';
import { useFindSlug } from 'dashboard409a/utilities';
import {
  clearPreviousWorkboardRoute,
  getPreviousWorkboardRoute,
  savePreviousWorkboardRoute,
} from 'dashboard409a/utilities/navigation';
import AppSwitcher from 'layouts/FirmBaseLayout/PrimaryNavigation/AppSwitcher';
import CompanyFilter from 'layouts/Main/components/Sidebar/components/FirmFilter';
import { useLogOutUser } from 'pages/Auth/hooks';
import { clearAppAutoSelect, getSafePreviousPortfolioRoute, saveAppAutoSelect } from 'pages/Auth/utils';
import lightTheme, { darkTheme } from 'theme';
import { MainContainer } from './components';
import WorkboardOptionsMenu from './components/WorkboardOptionsMenu/WorkboardOptionsMenu';
import { IDashboardLayout } from './types';
import { ISubMenu } from '../../components/Sidebar/components/TopMenu/types';

const DashboardLayout = ({ children }: IDashboardLayout) => {
  const { resolvedMode } = useThemeMode();
  const { zoomLevel } = useZoom();
  const [{ firmInfo }] = useStore() as UseStoreValues;
  const [isSidebarOpen] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [pageActions, setPageActions] = useState<PageActionsProps | null | undefined>();
  const [editMode, setEditMode] = useState(false);
  const [secondaryNavActionsNode, setSecondaryNavActionsNode] = useState<ReactNode>(null);
  const [tertiaryNavNode, setTertiaryNavNode] = useState<ReactNode>(null);
  const [filterSearch, setFilterSearch] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const currentSlug = useFindSlug();

  const logOutUser = useLogOutUser();

  const user = useSelector((state: RootStateType) => state.user);

  const userIsStaff = useMemo(() => {
    if (user) {
      return user.is_staff;
    }
    return false;
  }, [user]);

  const userIsSuperuser = useMemo(() => Boolean(user?.is_superuser), [user]);

  const userHasCompanies = useMemo(() => {
    if (user) {
      return (user.standalone_companies_permissions?.length ?? 0) > 0;
    }
    return false;
  }, [user]);

  const { currentCompany, showLoadingProgressBar = false } = useSelector((state: RootStateType) => state.global);

  useEffect(() => {
    if (!isSidebarOpen) {
      setFilterSearch('');
    }
  }, [isSidebarOpen]);

  // Global SCSS (`_general.scss`) keys off this to switch table body row borders — that file uses `!important` and overrides MUI theme.
  useEffect(() => {
    if (resolvedMode === 'dark') {
      document.documentElement.setAttribute('data-dashboard-scheme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-dashboard-scheme');
    }
    return () => {
      document.documentElement.removeAttribute('data-dashboard-scheme');
    };
  }, [resolvedMode]);

  // Save workboard route to localStorage for redirect after login
  useEffect(() => {
    if (location.pathname.startsWith('/workboard/') && location.pathname !== WORKBOARD_ACCOUNT_SETTINGS_URL) {
      savePreviousWorkboardRoute(location.pathname);
    }
  }, [location.pathname]);

  const bottomItems = useMemo(() => {
    const currentBottomItems = [] as IBottomMenuItems[];

    if (
      user?.standalone_companies_permissions &&
      user?.standalone_companies_permissions?.length > 0 &&
      currentCompany
    ) {
      let filteredCompanies = user?.standalone_companies_permissions?.filter(
        standaloneCompany => standaloneCompany.id !== (currentCompany.id ?? 0)
      );

      if (filterSearch) {
        filteredCompanies = filteredCompanies.filter(standaloneCompany => {
          const companyName = standaloneCompany.name.toLowerCase();
          return companyName.includes(filterSearch.toLowerCase());
        });
      }

      if (filterSearch && !filteredCompanies.length) {
        filteredCompanies = [
          {
            id: 0,
            name: 'Not found',
            slug: 'not-found',
            icon_url: '',
            is_active: false,
            logo_url: '',
          },
        ];
      }

      const subItems = filteredCompanies?.map(standaloneCompany => {
        if (standaloneCompany.name === 'Not found') {
          return {
            handleItemClick: () => {},
            slug: standaloneCompany.slug,
            title: standaloneCompany.name,
            disabled: true,
          };
        }

        return {
          handleItemClick: () => {
            navigate({
              pathname: dashboardCompanyOverviewUrl(standaloneCompany.slug),
              search: `?${new URLSearchParams({})}`,
            });
          },
          slug: standaloneCompany.slug,
          title: standaloneCompany.name,
        };
      });

      currentBottomItems.push({
        imageUrl: currentCompany?.icon_url ?? '',
        isLink: true,
        primaryText: currentCompany.name ?? '',
        secondaryText: VIEW_DETAILS,
        slug: dashboardCompanySettingsUrl(currentCompany.slug) ?? '',
        subItems,
        subItemsLabel: <CompanyFilter setFilterSearch={setFilterSearch} isCompanyFilter />,
      });
    }
    if (user.first_name && user.last_name) {
      currentBottomItems.push({
        accountSettingsPath: WORKBOARD_ACCOUNT_SETTINGS_URL,
        ...(user.is_staff || user.is_superuser ? { auditLogsPath: WORKBOARD_AUDIT_LOGS_URL } : {}),
        ...(user.is_superuser ? { assignmentRulesPath: WORKBOARD_ASSIGNMENT_RULES_URL } : {}),
        displayAvatar: true,
        handleButtonClick: () => logOutUser(),
        imageUrl: user.profile?.picture_url ?? '',
        primaryText: user.email ?? `${user.first_name} ${user.last_name}`.trim(),
        secondaryText: LOG_OUT,
        slug: slugify(`${user.first_name} ${user.last_name}`.trim()),
        ...(user.is_staff && (user.is_superuser || (user.firms_permissions?.length ?? 0) > 0)
          ? {
            switchAppLabel: 'Switch to Portfolio Valuation',
            onSwitchApp: () => {
              const savedPortfolioRoute = getSafePreviousPortfolioRoute(user.id);
              const singleFirmSlug = user.firms_permissions?.length === 1 ? user.firms_permissions[0]?.slug : null;

              // Authenticated product switches should jump straight to a resolved portfolio target; auth is only the fallback when a chooser is still needed.
              if (savedPortfolioRoute) {
                clearAppAutoSelect();
                navigate(savedPortfolioRoute);
                return;
              }

              if (singleFirmSlug) {
                clearAppAutoSelect();
                navigate(firmSummaryUrl(singleFirmSlug));
                return;
              }

              saveAppAutoSelect(PORTFOLIO_VALUATION);
              navigate(signInPath);
            },
          }
          : {}),
      });
    }

    return currentBottomItems;
  }, [currentCompany, logOutUser, user, filterSearch, navigate]);

  const reportsSubMenuItems: ISubMenu[] = useMemo(() => {
    const subMenuItems: ISubMenu[] = [
      { slug: 'reports', title: 'Reports', url: dashboardCompanyOverviewUrl(currentCompany?.slug ?? '') },
    ];
    if (userIsStaff) {
      subMenuItems.push({
        slug: 'operations',
        title: 'Operations',
        url: dashboardCompanyOperationsUrl(currentCompany?.slug ?? ''),
      });
    }
    if (userIsStaff) {
      subMenuItems.push({
        slug: 'information',
        title: 'Information',
        url: dashboardCompanyInformationUrl(currentCompany?.slug ?? ''),
      });
    }
    if (userIsSuperuser) {
      subMenuItems.push({
        slug: 'settings',
        title: 'Settings',
        url: dashboardCompanySettingsUrl(currentCompany?.slug ?? ''),
      });
    }
    return subMenuItems;
  }, [currentCompany, userIsStaff, userIsSuperuser]);

  const workboardSubMenuItems: ISubMenu[] = useMemo(
    () => [
      { slug: 'current-valuations', title: 'Current Valuations', url: WORKBOARD_CURRENT_VALUATIONS_URL },
      { slug: 'past-valuations', title: 'Past Valuations', url: WORKBOARD_PAST_VALUATIONS_URL },
      { slug: 'opportunities', title: 'Opportunities', url: WORKBOARD_OPPORTUNITIES_URL },
      { slug: 'models', title: 'Models', url: WORKBOARD_MODELS_URL },
    ],
    []
  );

  const topMenuItems = useMemo(
    () =>
      [
        {
          slug: 'workboard',
          title: 'Workboard',
          isParent: true,
          icon: <HomeIcon />,
          handleClick: () => {
            const savedRoute = getPreviousWorkboardRoute();
            if (savedRoute?.includes('/workboard/')) {
              navigate(savedRoute);
              clearPreviousWorkboardRoute();
            } else {
              navigate(WORKBOARD_CURRENT_VALUATIONS_URL);
            }
          },
          url: WORKBOARD_CURRENT_VALUATIONS_URL,
          subMenu: workboardSubMenuItems,
        },
        ...((userIsSuperuser || userIsStaff) && userHasCompanies && currentCompany
          ? [
            {
              slug: 'reports',
              title: 'Reports',
              isParent: true,
              icon: <AssignmentIcon />,
              handleClick: () => {
                navigate(dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''));
              },
              url: dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''),
              subMenu: reportsSubMenuItems,
            },
          ]
          : []),
      ] as ITopMenuItems[],
    [
      currentCompany,
      navigate,
      userIsStaff,
      workboardSubMenuItems,
      reportsSubMenuItems,
      userIsSuperuser,
      userHasCompanies,
    ]
  );

  const settingsMenuItems = useMemo(
    () =>
      [
        {
          handleClick: () => {
            navigate(dashboardCompanyOverviewUrl(currentCompany?.slug ?? ''));
          },
          url: dashboardCompanySettingsUrl(currentCompany?.slug ?? ''),
          isParent: true,
          slug: SETTINGS_SLUG,
          title: 'Settings',
          subMenu: [
            { slug: SETTINGS_SLUG, title: 'Settings', url: dashboardCompanySettingsUrl(currentCompany?.slug ?? '') },
            {
              slug: SUBSCRIPTION_SLUG,
              title: 'Subscription',
              url: dashboardCompanySubscriptionUrl(currentCompany?.slug ?? ''),
            },
          ],
        },
      ] as ITopMenuItems[],
    [currentCompany, navigate]
  );

  const navigationItems = useMemo(() => {
    const currentNavItems = [] as INavigationItem[];

    if (currentSlug) {
      if (location.pathname === WORKBOARD_ACCOUNT_SETTINGS_URL) {
        currentNavItems.push({
          id: 'account-settings',
          title: 'Account Settings',
          handleAction: () => navigate(WORKBOARD_ACCOUNT_SETTINGS_URL),
        } as INavigationItem);
        return currentNavItems;
      }

      if (location.pathname === WORKBOARD_AUDIT_LOGS_URL) {
        currentNavItems.push({
          id: AUDIT_LOGS_TAB,
          title: 'Audit Logs',
          handleAction: () => navigate(WORKBOARD_AUDIT_LOGS_URL),
        } as INavigationItem);
        return currentNavItems;
      }

      if (location.pathname === WORKBOARD_ASSIGNMENT_RULES_URL) {
        currentNavItems.push({
          id: 'assignment-rules',
          title: 'Assignment Rules',
          handleAction: () => navigate(WORKBOARD_ASSIGNMENT_RULES_URL),
        } as INavigationItem);
        return currentNavItems;
      }

      const menuSource = COMPANY_DETAILS_SLUGS.includes(currentSlug) ? settingsMenuItems : topMenuItems;
      const navMenu = menuSource.find(menuItem =>
        menuItem?.subMenu?.some(subItem => subItem.url.includes(`${currentSlug}`))
      )?.subMenu;

      navMenu?.forEach(nav => {
        currentNavItems.push({
          id: nav.slug,
          title: nav.title,
          handleAction: () => navigate(nav.url),
        } as INavigationItem);
      });
    }

    return currentNavItems;
  }, [currentSlug, settingsMenuItems, topMenuItems, navigate, location.pathname]);

  const updatePageActions = useCallback(
    (newActions: PageActionsProps | null | undefined) => {
      setPageActions(newActions);
    },
    [setPageActions]
  );

  const companyDashboardProviderValue = useMemo(
    () => ({
      pageActions: pageActions ?? undefined,
      updatePageActions,
      editMode,
      setEditMode,
      secondaryNavActionsNode: secondaryNavActionsNode ?? undefined,
      setSecondaryNavActionsNode,
      setTertiaryNavNode,
    }),
    [editMode, pageActions, secondaryNavActionsNode, updatePageActions]
  );

  const isOnWorkboard = location.pathname.startsWith('/workboard/');

  const activeTheme = resolvedMode === 'dark' ? darkTheme : lightTheme;

  const resolvedBackgroundColor = activeTheme.palette.background?.default ?? activeTheme.palette.white;

  return (
    <ThemeProvider theme={activeTheme}>
      {/* Override body background so it matches the active theme even in areas with no content */}
      <GlobalStyles
        styles={{
          'body, #root': {
            backgroundColor: `${resolvedBackgroundColor} !important`,
            color: activeTheme.palette.text.primary,
            transition: 'background-color 0.2s ease, color 0.2s ease',
          },
        }}
      />
      {/* Box ensures color cascades correctly into all MUI children */}
      <Box sx={{ color: 'text.primary', minHeight: '100vh' }}>
        <NotificationCenterProvider
          channelName=""
          enableGlobalChannel={Boolean(user?.id)}>
          <AttioValuationProvider>
            <CompanyDashboardContext.Provider value={companyDashboardProviderValue}>
              {userIsStaff && <DropboxOAuthCallbackHandler />}
              {!isOnWorkboard && (
                <Sidebar
                  topMenuItems={topMenuItems}
                  bottomMenuItems={bottomItems}
                  logoUrl={currentCompany?.logo_url ?? ''}
                  isLoading={showLoadingProgressBar}
                />
              )}
  
              {isOnWorkboard && (
                <>
                  <PrimaryNavigationPrimitive
                    brandSlot={(
                      <span style={{ display: 'inline-flex', height: '100%', width: '100%' }}>
                        {firmInfo?.icon_url ? (
                          <img
                            src={firmInfo.icon_url}
                            alt={firmInfo.name ?? 'Firm logo'}
                            style={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }}
                          />
                        ) : (
                          <Logo variation="small" />
                        )}
                      </span>
                    )}
                    items={[{ label: 'Workboard', value: 'workboard' }]}
                    value="workboard"
                    actionsSlot={(
                      <>
                        <NotificationBell zoomLevel={zoomLevel} />
                        {(user.is_staff || (user.firms_permissions?.length ?? 0) > 0) && (
                          <AppSwitcher zoomLevel={zoomLevel} activeApp="workboard" />
                        )}
                        <Tooltip placement="bottom" title={`${user.first_name} ${user.last_name}`.trim() || user.email}>
                          <span style={{ display: 'inline-flex' }}>
                            {user.profile?.picture_url ? (
                              <Avatar
                                src={user.profile.picture_url}
                                sx={theme => ({
                                  backgroundColor: theme.palette.neutral[300],
                                  border: `${getZoomAdjustedPx(2, zoomLevel)} solid ${theme.palette.brand[500]}`,
                                  boxSizing: 'border-box',
                                  height: getZoomAdjustedPx(32, zoomLevel),
                                  width: getZoomAdjustedPx(32, zoomLevel),
                                })}
                              />
                            ) : (
                              <AccountCircle sx={{ width: getZoomAdjustedPx(32, zoomLevel), height: getZoomAdjustedPx(32, zoomLevel) }} />
                            )}
                          </span>
                        </Tooltip>
                        <PrimaryNavigationIconButton
                          aria-label="Workboard options"
                          aria-haspopup="true"
                          aria-expanded={Boolean(optionsAnchorEl)}
                          isInteractive
                          onClick={(e: React.MouseEvent<HTMLElement>) => setOptionsAnchorEl(e.currentTarget)}
                          zoomLevel={zoomLevel}>
                          <MoreVert />
                        </PrimaryNavigationIconButton>
                      </>
                    )}
                    zoomLevel={zoomLevel}
                  />
                  <Box sx={{ '& > nav': { height: '48px' } }}>
                    <SecondaryNavigation
                      actions={secondaryNavActionsNode ?? undefined}
                      items={workboardSubMenuItems.map(item => ({ label: item.title, value: item.slug }))}
                      onChange={(_, slug) => {
                        const found = workboardSubMenuItems.find(i => i.slug === slug);
                        if (found) navigate(found.url);
                      }}
                      title="Workboard"
                      value={currentSlug || false}
                      zoomLevel={zoomLevel}
                    />
                  </Box>
                </>
              )}
  
              {isOnWorkboard && tertiaryNavNode}
  
              <MainContainer
                currentCompany={currentCompany}
                hideSidebar={isOnWorkboard}
                isLoading={showLoadingProgressBar}
                navigationItems={navigationItems}>
                {children}
              </MainContainer>
            </CompanyDashboardContext.Provider>
          </AttioValuationProvider>
        </NotificationCenterProvider>
      </Box>

      {isOnWorkboard && (
        <WorkboardOptionsMenu
          anchorEl={optionsAnchorEl}
          onClose={() => setOptionsAnchorEl(null)}
        />
      )}
    </ThemeProvider>
  );
};

export default DashboardLayout;
